import { useEffect, useRef, useState, type ReactNode } from "react";
import { FilterSelect } from "./Toolbar";
import { columnFilters, matchesColumnFilters, type ColumnFilter } from "@/lib/tableFilters";

/**
 * Wide tables scroll inside their own container — the page never scrolls
 * sideways. Rows are dense on purpose: a list of counters is scanned, not read.
 */
export function Table({
  children,
  fill = false,
  searchable = true,
  filterable = true,
  scrollable = false,
}: {
  children: ReactNode;
  /**
   * D-15 — this wrapper becomes the page's ONE scrolling region. Use inside a
   * `<Card fill>`. Column headings stay pinned while the rows move.
   */
  fill?: boolean;
  /** Pages with a toolbar search already filter their data before rendering. */
  searchable?: boolean;
  /** Disable when a paginated page supplies database-backed toolbar filters. */
  filterable?: boolean;
  /** Bound long detail tables to the viewport and expose an inner vertical scroll. */
  scrollable?: boolean;
}) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [facets, setFacets] = useState<ColumnFilter[]>([]);
  const [count, setCount] = useState({ visible: 0, total: 0 });

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    if (!searchable && !filterable) return;
    const update = () => {
      const terms = (searchable ? query : "").toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
      const rows = Array.from(table.tBodies).flatMap((body) => Array.from(body.rows));
      if (filterable) {
        const headings = Array.from(table.tHead?.rows[0]?.cells ?? []).map((cell) => cell.textContent?.trim() ?? "");
        const next = columnFilters(headings, rows.map((row) => Array.from(row.cells).map((cell) => cell.textContent?.trim() ?? "")));
        setFacets((previous) => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
      }
      let visible = 0;
      for (const row of rows) {
        const cells = Array.from(row.cells).map((cell) => cell.textContent?.toLocaleLowerCase() ?? "");
        const text = cells.join(" ");
        const matches = terms.every((term) => text.includes(term)) && (!filterable || matchesColumnFilters(Array.from(row.cells).map((cell) => cell.textContent ?? ""), filters));
        row.hidden = !matches;
        if (matches) visible++;
      }
      setCount((previous) => previous.visible === visible && previous.total === rows.length ? previous : { visible, total: rows.length });
    };
    update();
    // Rows may arrive asynchronously or refresh after an action.
    const observer = new MutationObserver(update);
    observer.observe(table, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [children, query, searchable, filterable, filters]);

  return (
    <div className={`flex flex-col ${fill ? "min-h-0 flex-1" : ""}`}>
      {(searchable || filterable && facets.length > 0) && <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-rule p-3">
        {searchable && <input aria-label="Search all table columns on this page" className="h-9 min-w-48 flex-1 rounded-md border border-rule bg-raised px-3 text-sm text-ink"
          type="search" placeholder="Search all columns on this page…" value={query} onChange={(e) => setQuery(e.target.value)} />}
        {filterable && facets.map((facet) => <FilterSelect key={facet.index} label={facet.label} options={facet.options}
          value={filters[facet.index] ?? ""} onChange={(value) => setFilters((previous) => { const next = { ...previous }; if (value) next[facet.index] = value; else delete next[facet.index]; return next; })} />)}
        {(query || Object.keys(filters).length > 0) && <>
          <span className="text-xs text-muted" role="status">{count.visible} of {count.total} rows on this page</span>
          <button className="text-sm text-brand-ink" type="button" onClick={() => { setQuery(""); setFilters({}); }}>Clear filters</button>
        </>}
      </div>}
      <div className={fill ? "min-h-0 flex-1 overflow-auto" : scrollable ? "max-h-[calc(100dvh-16rem)] overflow-auto" : "overflow-x-auto"}>
        <table ref={tableRef} className="w-full min-w-[38rem] border-collapse text-sm">{children}</table>
      </div>
      {count.total > 0 && count.visible === 0 && <p role="status" className="p-5 text-center text-sm text-muted">No rows match this search on this page.</p>}
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  // Sticky so the headings survive the row scroll they now sit above.
  return (
    <thead className="sticky top-0 z-10 bg-raised shadow-[inset_0_-1px_0_var(--gq-rule)] [&_tr]:bg-ink/[0.025]">
      {children}
    </thead>
  );
}

export function TH({
  children,
  align = "left",
  width,
}: {
  children?: ReactNode;
  align?: "left" | "right";
  width?: string;
}) {
  return (
    <th
      className={`h-10 px-4 text-[11px] font-semibold tracking-[0.08em] whitespace-nowrap text-muted uppercase first:pl-5 last:pr-5 ${
        align === "right" ? "text-right" : "text-left"
      }`}
      scope="col"
      style={width ? { width } : undefined}
    >
      {children}
    </th>
  );
}

export function TR({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <tr
      className={`border-b border-rule/70 transition-colors last:border-0 ${
        onClick ? "cursor-pointer hover:bg-brand-light/60" : "hover:bg-ink/[0.018]"
      }`}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

export function TD({
  children,
  align = "left",
  /** Use for any column of figures so digits line up between rows. */
  numeric = false,
  className = "",
}: {
  children?: ReactNode;
  align?: "left" | "right";
  numeric?: boolean;
  className?: string;
}) {
  return (
    <td
      className={[
        "h-[3.25rem] px-4 py-2 text-ink first:pl-5 last:pr-5",
        align === "right" ? "text-right" : "",
        numeric ? "tabular-nums" : "",
        className,
      ].join(" ")}
    >
      {children}
    </td>
  );
}

/**
 * Primary cell: a name with a quiet second line under it (an id, a slug, a
 * phone). Replaces the pattern of giving every secondary detail its own
 * column, which is what made the old tables so wide.
 */
export function TDStacked({
  primary,
  secondary,
  avatar,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  avatar?: ReactNode;
}) {
  return (
    <td className="h-[3.25rem] px-4 py-2 first:pl-5 last:pr-5">
      <div className="flex items-center gap-3">
        {avatar}
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{primary}</p>
          {secondary ? <p className="truncate text-[13px] text-muted">{secondary}</p> : null}
        </div>
      </div>
    </td>
  );
}

/** Initials disc, sized to sit inside a dense row without inflating it. */
export function Avatar({ name, src }: { name: string; src?: string | null }) {
  if (src) {
    return <img alt="" className="size-8 shrink-0 rounded-full object-cover" src={src} />;
  }
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <span className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-light font-title text-[11px] font-semibold text-brand-ink ring-1 ring-brand-border ring-inset">
      {initials}
    </span>
  );
}
